/* Adds fresh, explicitly scoped card metadata to the original CEIKA vehicle API response. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.CeikaVehicleCardData = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const MANIFEST_MAX_AGE_MS = 24 * 60 * 60 * 1000;
  const REQUEST_TIMEOUT_MS = 8000;
  const MAX_PRODUCTS_PER_GROUP = 100;
  const MAX_PARALLEL_DETAILS = 8;
  const MAX_CACHED_DETAILS = 200;
  const FAMILY = Object.freeze({ coilovers: 'coilovers', brakes: 'brakes', airride: 'airride', wheels: 'wheels' });
  let manifestURL = '';
  let manifestPromise = null;
  const detailCache = new Map();
  const detailPromises = new Map();
  const detailWaiters = [];
  let activeDetails = 0;

  function emptyEnrichment(group) {
    return { ...group, price: null, startingPrice: null, matchedProducts: [], reviewCount: null, reviewRating: null, reviewScope: null, reviewURL: group.reviewURL || group.url || null };
  }

  function currencyContext(context) {
    return context?.country === 'US' && context?.currency === 'USD';
  }

  function validManifest(manifest, context, now) {
    if (!manifest || manifest.schemaVersion !== 1) return false;
    const hasInlineProducts = manifest.products && typeof manifest.products === 'object' && !Array.isArray(manifest.products);
    const hasProductPath = typeof manifest.productDataPath === 'string' && manifest.productDataPath.length > 0;
    if (!hasInlineProducts && !hasProductPath) return false;
    if (!currencyContext(context) || manifest.context?.country !== context.country || manifest.context?.currency !== context.currency) return false;
    const fetchedAt = Date.parse(manifest.fetchedAt);
    return Number.isFinite(fetchedAt) && fetchedAt <= now && now - fetchedAt <= MANIFEST_MAX_AGE_MS;
  }

  async function withDetailLimit(task) {
    if (activeDetails >= MAX_PARALLEL_DETAILS) await new Promise(resolve => detailWaiters.push(resolve));
    activeDetails += 1;
    try { return await task(); }
    finally {
      activeDetails -= 1;
      detailWaiters.shift()?.();
    }
  }

  function cacheDetail(key, value) {
    detailCache.delete(key);
    detailCache.set(key, value);
    while (detailCache.size > MAX_CACHED_DETAILS) detailCache.delete(detailCache.keys().next().value);
  }

  async function resolveProductDetail(handle, manifest, cacheURL, signal) {
    if (!handle || !manifest) return null;
    if (manifest.products && !Array.isArray(manifest.products)) return manifest.products[handle] || null;
    if (!manifest.productDataPath) return null;
    let manifestAddress, directory, detailURL;
    try {
      const base = globalThis.location?.href || 'http://localhost/';
      manifestAddress = new URL(cacheURL, base);
      directory = new URL(manifest.productDataPath, manifestAddress);
      if (directory.origin !== manifestAddress.origin || (globalThis.location?.origin && directory.origin !== globalThis.location.origin)) return null;
      if (!directory.pathname.endsWith('/')) return null;
      detailURL = new URL(`${encodeURIComponent(handle)}.json`, directory).href;
    } catch (_) { return null; }

    const key = `${manifest.fetchedAt}|${handle}`;
    if (detailCache.has(key)) {
      const value = detailCache.get(key);
      detailCache.delete(key); detailCache.set(key, value);
      return value;
    }
    if (!detailPromises.has(key)) {
      const pending = withDetailLimit(async () => {
        const value = await requestJSON(detailURL, { credentials: 'same-origin', headers: { Accept: 'application/json' } }, signal);
        if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('product_detail_invalid');
        cacheDetail(key, value);
        return value;
      });
      detailPromises.set(key, pending);
      pending.catch(() => { if (detailPromises.get(key) === pending) detailPromises.delete(key); });
    }
    try { return await detailPromises.get(key); }
    catch (error) { if (signal?.aborted) throw error; return null; }
  }

  async function requestJSON(url, init, externalSignal) {
    if (externalSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const controller = new AbortController();
    let timedOut = false;
    let rejectExternalAbort;
    const onAbort = () => {
      controller.abort();
      rejectExternalAbort?.(new DOMException('Aborted', 'AbortError'));
    };
    if (externalSignal?.aborted) controller.abort();
    else externalSignal?.addEventListener('abort', onAbort, { once: true });
    const request = (async () => {
      const response = await fetch(url, { ...init, signal: controller.signal });
      if (!response.ok) throw new Error('card_data_unavailable');
      return response.json();
    })();
    let deadline;
    const timeout = new Promise((_, reject) => {
      deadline = setTimeout(() => { timedOut = true; controller.abort(); reject(new Error('card_data_timeout')); }, REQUEST_TIMEOUT_MS);
    });
    const cancellation = externalSignal ? new Promise((_, reject) => { rejectExternalAbort = reject; }) : null;
    try { return await Promise.race([request, timeout, ...(cancellation ? [cancellation] : [])]); }
    catch (error) {
      if (externalSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
      if (timedOut) throw new Error('card_data_timeout');
      throw error;
    } finally {
      clearTimeout(deadline);
      externalSignal?.removeEventListener('abort', onAbort);
    }
  }

  async function loadManifest(cacheURL) {
    if (!cacheURL) return null;
    if (manifestURL !== cacheURL) {
      manifestURL = cacheURL;
      manifestPromise = null;
    }
    if (!manifestPromise) {
      const base = globalThis.location?.href || 'http://localhost/';
      let address;
      try { address = new URL(cacheURL, base); }
      catch (_) { return null; }
      if (globalThis.location?.origin && address.origin !== globalThis.location.origin) return null;
      const pending = requestJSON(address.href, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      let wrapped;
      wrapped = pending.catch(error => {
        if (manifestPromise === wrapped) manifestPromise = null;
        throw error;
      });
      manifestPromise = wrapped;
    }
    try { return await manifestPromise; }
    catch (_) { return null; }
  }

  async function fetchProducts(api, vehicle, family, locale, signal) {
    const url = new URL(api.replace(/\/$/, '') + '/api/v1/products', globalThis.location?.href || 'http://localhost/');
    url.searchParams.set('vehicle_id', vehicle.id);
    if (Number.isInteger(vehicle.year)) url.searchParams.set('year', String(vehicle.year));
    url.searchParams.set('family', family);
    url.searchParams.set('sort', 'title');
    url.searchParams.set('offset', '0');
    url.searchParams.set('limit', String(MAX_PRODUCTS_PER_GROUP));
    if (locale) url.searchParams.set('locale', locale);
    const data = await requestJSON(url.href, { credentials: 'omit', headers: { Accept: 'application/json' } }, signal);
    if (!Array.isArray(data.products)) throw new Error('vehicle_products_invalid');
    const reportedTotal = Number(data.total);
    return { products: data.products.slice(0, MAX_PRODUCTS_PER_GROUP), total: reportedTotal, complete: Number.isSafeInteger(reportedTotal) && reportedTotal >= 0 && reportedTotal <= MAX_PRODUCTS_PER_GROUP && data.products.length === reportedTotal };
  }

  function listedPrice(product, entry) {
    if (!entry || entry.available !== true || entry.currency !== 'USD' || !Number.isSafeInteger(entry.amountMinor) || entry.amountMinor <= 0 || !['kit', 'axle kit'].includes(entry.basis)) return null;
    if (!product.handle || !entry.sourceURL) return null;
    return { amountMinor: entry.amountMinor, currency: 'USD', basis: entry.basis, sourceURL: entry.sourceURL, scope: 'matched', productTitle: product.title || null };
  }

  function safeReview(entry) {
    if (!entry || entry.reviewScope !== 'listing' || !Number.isSafeInteger(entry.reviewCount) || entry.reviewCount < 0) return null;
    const rating = Number.isFinite(entry.rating) && entry.rating >= 1 && entry.rating <= 5 ? entry.rating : null;
    return { count: entry.reviewCount, rating };
  }

  function reviewAnchor(sourceURL, fallbackURL) {
    const url = sourceURL || fallbackURL;
    return url ? `${url.split('#')[0]}#judgeme_product_reviews` : null;
  }

  async function enrichGroup(group, vehicle, context, signal, manifest, api, options) {
    const output = emptyEnrichment(group);
    const family = FAMILY[group.key];
    if (!family || !vehicle?.id || !api) return output;

    if (family === 'wheels') {
      const handle = options.wheelHandle;
      let product = null;
      try { product = await resolveProductDetail(handle, manifest, options.cacheURL, signal); }
      catch (error) { if (signal?.aborted) throw error; }
      if (!product || product.available !== true || product.currency !== 'USD' || !Number.isSafeInteger(product.amountMinor) || product.amountMinor <= 0 || product.basis !== 'wheel' || !product.sourceURL) return output;
      output.startingPrice = { amountMinor: product.amountMinor, currency: 'USD', basis: 'wheel', sourceURL: product.sourceURL, scope: 'featured', productTitle: product.title || 'Featured forged wheel', priceLabel: 'Per wheel from' };
      const review = safeReview(product);
      if (review) { output.reviewCount = review.count; output.reviewRating = review.rating; output.reviewScope = 'listing'; output.reviewURL = reviewAnchor(product.sourceURL); }
      output.matchedProducts = [{ handle, title: product.title || 'Featured forged wheel', url: product.sourceURL, scope: 'featured' }];
      return output;
    }

    const data = await fetchProducts(api, vehicle, family, context?.locale, signal);
    output.matchedProducts = data.products.map(product => ({ handle: product.handle || product.id || null, title: product.title || '', url: product.url || null }));
    if (!data.complete || !data.products.length) return output;
    const expectedCount = Number(group.matchedProductCount);
    if (!Number.isSafeInteger(expectedCount) || expectedCount !== data.total) return output;
    const entries = await Promise.all(data.products.map(product => resolveProductDetail(product.handle || product.id, manifest, options.cacheURL, signal)));
    const prices = data.products.map((product, index) => listedPrice(product, entries[index]));
    if (prices.every(Boolean)) {
      const minIndex = prices.reduce((bestIndex, current, index) => current.amountMinor < prices[bestIndex].amountMinor ? index : bestIndex, 0);
      output.startingPrice = prices[minIndex];
      const review = safeReview(entries[minIndex]);
      if (review) {
        output.reviewCount = review.count;
        output.reviewRating = review.rating;
        output.reviewScope = 'listing';
        output.reviewURL = reviewAnchor(entries[minIndex].sourceURL, data.products[minIndex].url || group.url);
      }
    }
    return output;
  }

  async function enrich(groups, vehicle, context, signal, options = {}) {
    const source = Array.isArray(groups) ? groups : [];
    const safeGroups = source.map(emptyEnrichment);
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const manifest = await loadManifest(options.cacheURL);
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const trustedManifest = validManifest(manifest, context, Date.now()) ? manifest : null;
    const api = options.api || '';
    const results = await Promise.all(safeGroups.map(async (group, index) => {
      try { return await enrichGroup(group, vehicle, context, signal, trustedManifest, api, options); }
      catch (error) { if (signal?.aborted) throw error; return safeGroups[index]; }
    }));
    return results;
  }

  return { enrich, constants: { manifestMaxAgeMs: MANIFEST_MAX_AGE_MS, maxProductsPerGroup: MAX_PRODUCTS_PER_GROUP, maxParallelDetails: MAX_PARALLEL_DETAILS, maxCachedDetails: MAX_CACHED_DETAILS, requestTimeoutMs: REQUEST_TIMEOUT_MS } };
});